import { useQuery } from "@apollo/client";
import Close from "@mui/icons-material/Close";
import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { GridPaginationModel } from "@mui/x-data-grid-premium";
import DonutChart from "components/Dashboard/DonutChart";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { gql } from "generated-graphql";
import {
  GetScoutConversationContextAnalysisForStaffQuery,
  GetScoutConversationContextAnalysisForStaffQueryVariables,
} from "generated-graphql/graphql";
import { useEffect, useMemo, useRef, useState } from "react";

export const GET_SCOUT_CONVERSATION_CONTEXT_ANALYSIS_FOR_STAFF = gql(`
  query GetScoutConversationContextAnalysisForStaff(
    $conversationId: ID!
    $page: Int
    $pageSize: Int
    $partType: String
  ) {
    getScoutConversationContextAnalysisForStaff(
      conversationId: $conversationId
      page: $page
      pageSize: $pageSize
      partType: $partType
    ) {
      conversationId
      count
      totalEstimatedTokens
      breakdown {
        type
        toolName
        count
        estimatedTokens
      }
      cache {
        cacheReadTokens
        cacheWriteTokens
        totalInputTokens
      }
      turns {
        messageId
        seq
        turnNumber
        roundtripIndex
        requestId
        traceId
        createdAt
        provider
        model
        inputTokens
        outputTokens
        thinkingTokens
        cacheReadTokens
        cacheWriteTokens
        systemPromptTokens
        toolDefinitionsTokens
        systemPromptText
        toolDefinitionsJson
        allowedSkillsJson
        conversationHistoryTokens
        estimatedCostUsd
      }
      items {
        id
        conversationId
        messageId
        seq
        type
        streamPartId
        stepIndex
        agentState
        text
        toolName
        toolDisplayName
        toolCallId
        toolState
        toolInput
        toolOutput
        toolError
        toolTokenEstimate
        toolInputTokens
        toolDurationMs
        sourceId
        url
        title
        mediaType
        filename
        createdAt
        estimatedTokens
        message {
          id
          role
          seq
          requestId
          createdAt
        }
      }
    }
  }
`);

type PartRow = NonNullable<
  GetScoutConversationContextAnalysisForStaffQuery["getScoutConversationContextAnalysisForStaff"]
>["items"][number];
type TurnRow = NonNullable<
  GetScoutConversationContextAnalysisForStaffQuery["getScoutConversationContextAnalysisForStaff"]
>["turns"][number];

// Wider list than usual because the parts table is a deep-inspection tool —
// staff often want 50–100 rows visible at once without clicking through
// pages. The DataGrid's hidden "all" sentinel (-1) is what we ship as a
// null pageSize to the server when the user picks it.
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 250];
const SHOW_ALL_PAGE_SIZE = -1;
const SENTRY_ORG_SLUG = "encamp-98d563ed0";

interface Props {
  conversationId: string;
}

function buildSentryTraceUrl(traceId: string): string {
  return `https://${SENTRY_ORG_SLUG}.sentry.io/explore/traces/trace/${traceId}`;
}

export function ScoutConversationContextAnalysis({ conversationId }: Props) {
  const theme = useTheme();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [activePart, setActivePart] = useState<PartRow | null>(null);

  const showAll = paginationModel.pageSize === SHOW_ALL_PAGE_SIZE;

  const variables: GetScoutConversationContextAnalysisForStaffQueryVariables = {
    conversationId,
    page: showAll ? null : paginationModel.page,
    pageSize: showAll ? null : paginationModel.pageSize,
    // No part-type filter from the donut: slices represent SDK attribution
    // categories, not ScoutMessagePart types. The parts table stays
    // unfiltered so staff can see the whole stream.
    partType: null,
  };

  const { data, loading, error } = useQuery(GET_SCOUT_CONVERSATION_CONTEXT_ANALYSIS_FOR_STAFF, {
    variables,
    fetchPolicy: "cache-and-network",
  });

  const result = data?.getScoutConversationContextAnalysisForStaff;

  // Hold the last-known total part count steady across refetches. MUI's
  // DataGrid resets paginationModel.page to 0 the moment its rowCount prop
  // implies the current page is out of bounds — and during a server-paginated
  // refetch `data` is undefined, which would otherwise collapse rowCount to 0
  // and bounce us back to page 0 (then trigger a second refetch for that
  // page, then settle on whichever wins the race). Keeping rowCount sticky
  // breaks the loop. Reset only when the conversation changes.
  const lastKnownRowCountRef = useRef<number>(0);
  useEffect(() => {
    lastKnownRowCountRef.current = 0;
  }, [conversationId]);
  if (typeof result?.count === "number") {
    lastKnownRowCountRef.current = result.count;
  }
  const stableRowCount = lastKnownRowCountRef.current;

  // Roll up the per-(type, toolName) breakdown into per-type slices for
  // the donut. The aggregator emits one row per (partType, toolName) for
  // tool slices and one row per synthetic bucket (system_prompt, etc.).
  // We collapse the per-toolName rows back into a single slice per type so
  // the donut shows at most one segment per category — keeping tool
  // subtypes in the table where staff can still click to inspect each.
  //
  // Depend on `result?.breakdown` directly rather than aliasing with a
  // fallback `?? []` — the alias would synthesise a new empty array
  // every render and trip react-hooks/exhaustive-deps; reading the raw
  // (possibly-undefined) reference is stable when the query is in flight.
  const typeSlices = useMemo(() => {
    const items = result?.breakdown ?? [];
    const byType = new Map<string, number>();
    for (const item of items) {
      byType.set(item.type, (byType.get(item.type) ?? 0) + item.estimatedTokens);
    }
    return Array.from(byType.entries())
      .filter(([, tokens]) => tokens > 0)
      .map(([type, tokens]) => ({ type, tokens }))
      .sort((a, b) => b.tokens - a.tokens);
  }, [result?.breakdown]);

  // Tokens explicitly attributed to per-turn fixed overhead. Surface this
  // alongside the chart so staff can quickly see "of the 240k input
  // tokens, X were system prompt + tool definitions".
  const overheadTokens = useMemo(() => {
    const items = result?.breakdown ?? [];
    let sum = 0;
    for (const item of items) {
      if (item.type === "system_prompt" || item.type === "tool_definitions") {
        sum += item.estimatedTokens;
      }
    }
    return sum;
  }, [result?.breakdown]);

  const totalTokens = result?.totalEstimatedTokens ?? 0;
  const selectedIndex =
    selectedType != null ? typeSlices.findIndex((slice) => slice.type === selectedType) : -1;

  const seriesData = typeSlices.map((slice) => ({
    id: slice.type,
    value: slice.tokens,
    label: prettyPartType(slice.type),
  }));

  const handleDonutClick = (_event: unknown, item: { dataIndex: number }) => {
    const slice = typeSlices[item.dataIndex];
    if (!slice) {
      return;
    }
    // Slices represent SDK attribution categories (system_prompt,
    // tool_definitions, conversation_history, assistant_output, thinking)
    // and don't correspond to ScoutMessagePart types — clicking only
    // highlights the slice, the parts table stays unfiltered.
    setSelectedType((current) => (current === slice.type ? null : slice.type));
  };

  // Contiguous "#" column instead of the raw ScoutMessagePart.seq —
  // ScoutMessage and ScoutMessagePart share a sequence counter, so showing
  // the raw seq exposes apparent gaps (1, 4, 5, 8…) that look like data
  // loss. Position is rebuilt from the current page offset so it stays
  // stable across pagination.
  const pageOffset = showAll ? 0 : paginationModel.page * paginationModel.pageSize;
  // The result.items reference itself changes each render until Apollo
  // settles, so depend on result?.items directly (stable when undefined,
  // and Apollo holds the reference stable between settled fetches) rather
  // than aliasing through a freshly-allocated `?? []`.
  const indexByPartId = useMemo(() => {
    const map = new Map<string, number>();
    (result?.items ?? []).forEach((row, idx) => map.set(row.id, pageOffset + idx + 1));
    return map;
  }, [result?.items, pageOffset]);

  const columns = useMemo<CustomGridColDef<PartRow>[]>(
    () => [
      {
        field: "position",
        headerName: "#",
        width: 60,
        type: "number",
        valueGetter: (_value, row) => indexByPartId.get(row.id) ?? row.seq,
      },
      {
        field: "type",
        headerName: "Type",
        width: 140,
        valueGetter: (_value, row) => prettyPartType(row.type),
      },
      {
        field: "role",
        headerName: "Role",
        width: 90,
        valueGetter: (_value, row) => row.message.role,
      },
      {
        field: "tool",
        headerName: "Tool",
        width: 180,
        valueGetter: (_value, row) => row.toolDisplayName ?? row.toolName ?? "—",
      },
      {
        field: "preview",
        headerName: "Preview",
        flex: 1,
        minWidth: 240,
        sortable: false,
        valueGetter: (_value, row) => previewForPart(row),
      },
      {
        field: "estimatedTokens",
        headerName: "Tokens",
        width: 100,
        type: "number",
        valueGetter: (_value, row) => row.estimatedTokens,
        valueFormatter: (value) => (Number(value) > 0 ? Number(value).toLocaleString() : "—"),
      },
    ],
    [indexByPartId]
  );

  // First-turn prelude (system prompt + tool definitions JSON). Staff care
  // about what's in the prompt, not just its size — surface a card above
  // the table with a snippet and a "View full" button that opens the same
  // raw-content dialog the parts table uses.
  const firstTurnPrelude = useMemo(() => {
    const turns = result?.turns ?? [];
    for (const turn of turns) {
      if (turn.systemPromptText || turn.toolDefinitionsJson) {
        return turn;
      }
    }
    return null;
  }, [result?.turns]);
  type SubTab = "parts" | "roundtrips" | "turns" | "system" | "tools";
  const [subTab, setSubTab] = useState<SubTab>("parts");

  const cacheHitPct =
    result?.cache && result.cache.totalInputTokens > 0
      ? Math.round(
          ((result.cache.cacheReadTokens + result.cache.cacheWriteTokens) /
            result.cache.totalInputTokens) *
            100
        )
      : null;

  const totalCostUsd = useMemo(() => {
    const turns = result?.turns ?? [];
    return turns.reduce((sum, t) => sum + (t.estimatedCostUsd ?? 0), 0);
  }, [result?.turns]);

  return (
    // Flex column that exactly fills the parent: header + sub-tab strip +
    // active sub-tab content. The active tab uses flex:1 + overflow:hidden
    // and any data grid inside it virtualises its own scrollbar — no
    // double-scroll, no arbitrary height caps.
    <Stack spacing={2} sx={{ width: "100%", height: "100%", minHeight: 0, overflow: "hidden" }}>
      {error ? (
        <Alert severity="error">Failed to load context analysis: {error.message}</Alert>
      ) : null}

      <Paper
        variant="outlined"
        sx={{ p: 2, display: "flex", gap: 2, flexShrink: 0, alignItems: "stretch" }}
      >
        <Box
          sx={{
            width: { xs: "100%", md: 320 },
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Typography variant="subtitle2">Context usage</Typography>
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary">
              {totalTokens.toLocaleString()} tokens
            </Typography>
          </Stack>
          {seriesData.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: "center" }}>
              No context tokens recorded yet.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <DonutChart
                containerHeight="200px"
                seriesData={seriesData}
                seriesOverrides={{ innerRadius: 56, outerRadius: 90 }}
                selectedIndex={selectedIndex < 0 ? null : selectedIndex}
                onItemClick={handleDonutClick}
                legend={{ position: "below", direction: "row" }}
                colors={DONUT_COLORS}
                legendSx={{ flexWrap: "wrap", rowGap: 0.5, maxWidth: "100%", px: 0 }}
              />
            </Box>
          )}
          <Typography variant="caption" color="text.secondary">
            Slices partition input + output + thinking. Cache reads/writes are subsets of input —
            shown separately below.
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 6, md: 4, lg: 3 }}>
              <ContextStatCard
                label="Total tokens"
                value={totalTokens.toLocaleString()}
                hint={
                  result?.turns
                    ? `${result.turns.length.toLocaleString()} turn${
                        result.turns.length === 1 ? "" : "s"
                      }`
                    : undefined
                }
              />
            </Grid>
            <Grid size={{ xs: 6, md: 4, lg: 3 }}>
              <ContextStatCard
                label="Estimated cost"
                value={totalCostUsd > 0 ? `$${totalCostUsd.toFixed(4)}` : "—"}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 4, lg: 3 }}>
              <ContextStatCard
                label="Cache hit"
                value={cacheHitPct != null ? `${cacheHitPct}%` : "—"}
                hint={
                  result?.cache && result.cache.totalInputTokens > 0
                    ? `${result.cache.cacheReadTokens.toLocaleString()} read · ${result.cache.cacheWriteTokens.toLocaleString()} write`
                    : undefined
                }
              />
            </Grid>
            <Grid size={{ xs: 6, md: 4, lg: 3 }}>
              <ContextStatCard
                label="Per-turn overhead"
                value={overheadTokens > 0 ? overheadTokens.toLocaleString() : "—"}
                hint="System prompt + tool defs"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4, lg: 3 }}>
              <ContextStatCard
                label="Input"
                value={(result?.cache?.totalInputTokens ?? 0).toLocaleString()}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4, lg: 3 }}>
              <ContextStatCard
                label="Output"
                value={(
                  result?.turns?.reduce((s, t) => s + (t.outputTokens ?? 0), 0) ?? 0
                ).toLocaleString()}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4, lg: 3 }}>
              <ContextStatCard
                label="Thinking"
                value={(
                  result?.turns?.reduce((s, t) => s + (t.thinkingTokens ?? 0), 0) ?? 0
                ).toLocaleString()}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4, lg: 3 }}>
              <ContextStatCard label="Parts" value={(result?.count ?? 0).toLocaleString()} />
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Box sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
        <Tabs
          value={subTab}
          onChange={(_, v: SubTab) => setSubTab(v)}
          sx={{ minHeight: 40, "& .MuiTab-root": { minHeight: 40 } }}
        >
          <Tab value="parts" label={`Message parts${result ? ` (${result.count})` : ""}`} />
          <Tab
            value="roundtrips"
            label={`Per-roundtrip detail${result?.turns ? ` (${result.turns.length})` : ""}`}
          />
          <Tab
            value="turns"
            label={`Per-turn rollup${
              result?.turns ? ` (${countDistinctTurns(result.turns)})` : ""
            }`}
          />
          <Tab
            value="system"
            label={`System prompt${
              firstTurnPrelude?.systemPromptTokens
                ? ` · ${firstTurnPrelude.systemPromptTokens.toLocaleString()}`
                : ""
            }`}
          />
          <Tab
            value="tools"
            label={`Tool definitions${
              firstTurnPrelude?.toolDefinitionsTokens
                ? ` · ${firstTurnPrelude.toolDefinitionsTokens.toLocaleString()}`
                : ""
            }`}
          />
        </Tabs>
      </Box>

      <Paper
        variant="outlined"
        sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        {subTab === "parts" ? (
          <DataGrid<PartRow>
            rows={result?.items ?? []}
            getRowId={(row) => row.id}
            columns={columns}
            loading={loading && !result}
            paginationMode="server"
            pagination
            rowCount={stableRowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[
              ...PAGE_SIZE_OPTIONS,
              { value: SHOW_ALL_PAGE_SIZE, label: "Show all" },
            ]}
            onRowClick={(params) => setActivePart(params.row)}
            disableRowSelectionOnClick
            noRowsMessage="No parts persisted on this conversation yet."
            sx={{ flex: 1, border: "none" }}
          />
        ) : null}
        {subTab === "roundtrips" ? (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <RoundtripUsageTable rows={result?.turns ?? []} />
          </Box>
        ) : null}
        {subTab === "turns" ? (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <TurnRollupTable rows={rollupRoundtripsByTurn(result?.turns ?? [])} />
          </Box>
        ) : null}
        {subTab === "system" ? (
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            {firstTurnPrelude?.allowedSkillsJson ? (
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderBottom: 1,
                  borderColor: "divider",
                  backgroundColor: "action.hover",
                  flexShrink: 0,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Skill allow-list for this turn:{" "}
                  <Box component="span" sx={{ fontFamily: "monospace" }}>
                    {firstTurnPrelude.allowedSkillsJson}
                  </Box>
                </Typography>
              </Box>
            ) : null}
            <PreludePane
              text={firstTurnPrelude?.systemPromptText ?? null}
              empty="No system prompt recorded yet — turns persisted before the prelude rollout don't have content. Send a new message to populate."
            />
          </Box>
        ) : null}
        {subTab === "tools" ? (
          <PreludePane
            text={
              firstTurnPrelude?.toolDefinitionsJson
                ? prettyJson(firstTurnPrelude.toolDefinitionsJson)
                : null
            }
            empty="No tool definitions recorded yet — see the System prompt tab note."
          />
        ) : null}
      </Paper>
      <Dialog
        open={Boolean(activePart)}
        onClose={() => setActivePart(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          <span>
            Part #{activePart?.seq} — {activePart ? prettyPartType(activePart.type) : ""}
          </span>
          <IconButton size="small" onClick={() => setActivePart(null)}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {activePart ? (
            <Box
              component="pre"
              sx={{
                margin: 0,
                fontFamily: "monospace",
                fontSize: 12,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                backgroundColor: theme.palette.action.hover,
                p: 1.5,
                borderRadius: 1,
              }}
            >
              {JSON.stringify(activePart, null, 2)}
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
    </Stack>
  );
}

// Single-stat tile for the context summary card — matches the
// ScoutDashboardStatCard look so the page reads as part of the same
// observability family. Uses an `outlined` paper with a label + value, no
// height/width caps so the parent Grid controls layout.
function ContextStatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, height: "100%" }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2, mt: 0.25 }}>
        {value}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      ) : null}
    </Paper>
  );
}

// Full-pane reader for the system-prompt and tool-definitions sub-tabs.
// Renders the raw text in a monospace block with its own scrollbar so the
// parent layout doesn't have to worry about overflow.
function PreludePane({ text, empty }: { text: string | null; empty: string }) {
  if (!text) {
    return (
      <Box sx={{ p: 4, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography
          variant="body2"
          color="text.disabled"
          sx={{ maxWidth: 480, textAlign: "center" }}
        >
          {empty}
        </Typography>
      </Box>
    );
  }
  return (
    <Box
      component="pre"
      sx={{
        flex: 1,
        minHeight: 0,
        margin: 0,
        p: 2,
        overflow: "auto",
        fontFamily: "monospace",
        fontSize: 12,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {text}
    </Box>
  );
}

// Pretty-print a stored JSON string. Used by the tool-definitions prelude
// dialog so the JSON is readable instead of a single very long line. Falls
// back to the raw text if parsing fails (shouldn't happen — we wrote it
// with JSON.stringify on the way in — but worth being defensive).
function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

// Map raw ScoutMessagePartType + synthetic donut-bucket tokens to friendlier
// labels. Lowercase snake_case stays in the data layer for SQL stability;
// the UI gets a Title Case rendering. The "synthetic" buckets at the bottom
// of this switch don't correspond to a ScoutMessagePartType row — they come
// from per-turn totals on ScoutMessage (system prompt, tool definitions,
// cache reads, etc.) and exist so a staff user can attribute a large
// SDK-reported input total.
function prettyPartType(type: string): string {
  switch (type) {
    case "text":
      return "Text";
    case "reasoning":
      return "Reasoning";
    case "tool":
      return "Tool";
    case "source_url":
      return "Source · URL";
    case "source_document":
      return "Source · Document";
    case "file":
      return "File";
    case "step_start":
      return "Step start";
    case "abort":
      return "Abort";
    // Synthetic donut buckets (SDK-attribution categories).
    case "system_prompt":
      return "System prompt";
    case "tool_definitions":
      return "Tool definitions";
    case "conversation_history":
      return "Conversation history";
    case "assistant_output":
      return "Assistant output";
    case "thinking":
      return "Thinking";
    default:
      return type;
  }
}

function previewForPart(part: PartRow): string {
  if (part.text) {
    return part.text;
  }
  if (part.toolDisplayName || part.toolName) {
    return `${part.toolDisplayName ?? part.toolName}${part.toolState ? ` · ${part.toolState}` : ""}`;
  }
  if (part.url) {
    return part.url;
  }
  if (part.filename) {
    return part.filename;
  }
  return "—";
}

// Count of distinct turns within a roundtrip set — the "Per-turn rollup"
// tab needs a quick label of how many user-initiated turns this
// conversation has, derived from the per-roundtrip rows.
function countDistinctTurns(rows: TurnRow[]): number {
  return new Set(rows.map((r) => r.turnNumber ?? r.seq)).size;
}

// Roll up roundtrips by turn so the "Per-turn rollup" tab can show one
// row per user-initiated turn with summed totals. The model is pinned
// from the last roundtrip in the turn (a turn rarely switches models
// mid-flight, but the last value is the most recently observed truth).
function rollupRoundtripsByTurn(rows: TurnRow[]): TurnRollupRow[] {
  const byTurn = new Map<number, TurnRow[]>();
  for (const row of rows) {
    const turnNumber = row.turnNumber ?? row.seq;
    const bucket = byTurn.get(turnNumber);
    if (bucket) bucket.push(row);
    else byTurn.set(turnNumber, [row]);
  }
  return Array.from(byTurn.entries())
    .sort(([a], [b]) => a - b)
    .map(([turnNumber, group]) => {
      const sum = (k: keyof TurnRow) => group.reduce((acc, r) => acc + (Number(r[k] ?? 0) || 0), 0);
      return {
        turnNumber,
        roundtrips: group.length,
        model: group[group.length - 1]?.model ?? null,
        traceId: group.find((row) => row.traceId)?.traceId ?? null,
        inputTokens: sum("inputTokens"),
        outputTokens: sum("outputTokens"),
        thinkingTokens: sum("thinkingTokens"),
        cacheReadTokens: sum("cacheReadTokens"),
        cacheWriteTokens: sum("cacheWriteTokens"),
        systemPromptTokens: group[0]?.systemPromptTokens ?? 0,
        toolDefinitionsTokens: group[0]?.toolDefinitionsTokens ?? 0,
        estimatedCostUsd: sum("estimatedCostUsd"),
      };
    });
}

interface TurnRollupRow {
  turnNumber: number;
  roundtrips: number;
  model: string | null;
  traceId: string | null;
  inputTokens: number;
  outputTokens: number;
  thinkingTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  systemPromptTokens: number;
  toolDefinitionsTokens: number;
  estimatedCostUsd: number;
}

// Per-turn rollup table — one row per user-initiated turn, summing the
// roundtrips inside it. Complements RoundtripUsageTable which shows the
// per-call detail.
function TurnRollupTable({ rows }: { rows: TurnRollupRow[] }) {
  const columns = useMemo<CustomGridColDef<TurnRollupRow>[]>(
    () => [
      { field: "turnNumber", headerName: "Turn", width: 60, type: "number" },
      { field: "roundtrips", headerName: "Calls", width: 70, type: "number" },
      {
        field: "model",
        headerName: "Model",
        width: 170,
        valueGetter: (_value, row) => row.model ?? "—",
      },
      {
        field: "traceId",
        headerName: "Trace",
        width: 70,
        sortable: false,
        filterable: false,
        renderCell: (params) =>
          params.row.traceId ? (
            <Tooltip title="Open Sentry trace">
              <IconButton
                component="a"
                href={buildSentryTraceUrl(params.row.traceId)}
                target="_blank"
                rel="noreferrer"
                size="small"
                aria-label="Open Sentry trace"
              >
                <OpenInNew fontSize="inherit" />
              </IconButton>
            </Tooltip>
          ) : (
            "—"
          ),
      },
      {
        field: "inputTokens",
        headerName: "Input",
        width: 90,
        type: "number",
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "outputTokens",
        headerName: "Output",
        width: 90,
        type: "number",
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "thinkingTokens",
        headerName: "Thinking",
        width: 100,
        type: "number",
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "cacheReadTokens",
        headerName: "Cache read",
        width: 110,
        type: "number",
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "cacheWriteTokens",
        headerName: "Cache write",
        width: 110,
        type: "number",
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "estimatedCostUsd",
        headerName: "Cost",
        width: 100,
        type: "number",
        valueFormatter: (value) => (Number(value) > 0 ? `$${Number(value).toFixed(4)}` : "—"),
      },
    ],
    []
  );
  return (
    <DataGrid<TurnRollupRow>
      rows={rows}
      getRowId={(row) => row.turnNumber}
      columns={columns}
      hideFooter
      disableRowSelectionOnClick
      noRowsMessage="No turns yet."
      sx={{ border: "none", height: "100%" }}
    />
  );
}

// Per-roundtrip detail table. Each row is one provider HTTP call,
// labeled by `turnNumber.roundtripIndex` so staff can see exactly which
// call produced which usage vector. Uses the project's DataGrid wrapper
// to match the rest of the staff app (sticky header, numeric
// right-alignment, no-rows overlay).
function RoundtripUsageTable({ rows: turns }: { rows: TurnRow[] }) {
  const columns = useMemo<CustomGridColDef<TurnRow>[]>(
    () => [
      {
        field: "seq",
        headerName: "Turn.RT",
        width: 80,
        valueGetter: (_value, row) =>
          row.turnNumber != null && row.roundtripIndex != null
            ? `${row.turnNumber}.${row.roundtripIndex}`
            : `${row.seq}`,
      },
      {
        field: "model",
        headerName: "Model",
        width: 170,
        valueGetter: (_value, row) => row.model ?? "—",
      },
      {
        field: "traceId",
        headerName: "Trace",
        width: 70,
        sortable: false,
        filterable: false,
        renderCell: (params) =>
          params.row.traceId ? (
            <Tooltip title="Open Sentry trace">
              <IconButton
                component="a"
                href={buildSentryTraceUrl(params.row.traceId)}
                target="_blank"
                rel="noreferrer"
                size="small"
                aria-label="Open Sentry trace"
              >
                <OpenInNew fontSize="inherit" />
              </IconButton>
            </Tooltip>
          ) : (
            "—"
          ),
      },
      {
        field: "inputTokens",
        headerName: "Input",
        width: 90,
        type: "number",
        valueGetter: (_value, row) => row.inputTokens ?? 0,
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "systemPromptTokens",
        headerName: "System prompt",
        width: 130,
        type: "number",
        valueGetter: (_value, row) => row.systemPromptTokens ?? 0,
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "toolDefinitionsTokens",
        headerName: "Tool defs",
        width: 110,
        type: "number",
        valueGetter: (_value, row) => row.toolDefinitionsTokens ?? 0,
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "conversationHistoryTokens",
        headerName: "History",
        width: 100,
        type: "number",
        valueGetter: (_value, row) => row.conversationHistoryTokens ?? 0,
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "outputTokens",
        headerName: "Output",
        width: 90,
        type: "number",
        valueGetter: (_value, row) => row.outputTokens ?? 0,
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "thinkingTokens",
        headerName: "Thinking",
        width: 100,
        type: "number",
        valueGetter: (_value, row) => row.thinkingTokens ?? 0,
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "cacheReadTokens",
        headerName: "Cache read",
        width: 110,
        type: "number",
        valueGetter: (_value, row) => row.cacheReadTokens ?? 0,
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "cacheWriteTokens",
        headerName: "Cache write",
        width: 110,
        type: "number",
        valueGetter: (_value, row) => row.cacheWriteTokens ?? 0,
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "estimatedCostUsd",
        headerName: "Cost",
        width: 100,
        type: "number",
        valueGetter: (_value, row) => row.estimatedCostUsd,
        valueFormatter: (value) => (Number(value) > 0 ? `$${Number(value).toFixed(4)}` : "—"),
      },
    ],
    []
  );

  return (
    <DataGrid<TurnRow>
      rows={turns}
      getRowId={(row) => row.messageId}
      columns={columns}
      hideFooter
      disableRowSelectionOnClick
      noRowsMessage="No assistant turns yet."
      sx={{ border: "none", height: "100%" }}
    />
  );
}

// Compact "12.3k" / "1.2M" style label for the per-turn table. Mirrors the
// pricing.formatTokens helper but kept inline so the table doesn't depend
// on the scout-core runtime.
function formatTokens(tokens: number): string {
  if (!Number.isFinite(tokens) || tokens <= 0) {
    return "—";
  }
  if (tokens >= 1_000_000) {
    return `${(tokens / 1_000_000).toFixed(1)}M`;
  }
  if (tokens >= 1_000) {
    return `${Math.round(tokens / 100) / 10}K`;
  }
  return tokens.toString();
}

// Categorical palette chosen so adjacent slices stay visually distinct
// even with a colorblind viewer. Mirrors the Dashboard palette used
// elsewhere in the staff app.
const DONUT_COLORS = [
  "#1f77b4",
  "#ff7f0e",
  "#2ca02c",
  "#d62728",
  "#9467bd",
  "#8c564b",
  "#e377c2",
  "#7f7f7f",
];
